import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Department } from '@fewerbags/schema';
import { Alert } from '../Alert';
import { Button } from '../Button';
import { api } from '../../lib/api';
import { messageForError } from '../../lib/errors';
import { storeQueryKeys } from '../../pages/stores/queryKeys';
import { DEPARTMENT_LABEL } from './labels';

/** Reorder the walking order for one store with simple up/down buttons — no
 *  drag-and-drop dependency, and it works fine one-handed in a store aisle. */
export function DepartmentOrderEditor({
  storeId,
  order,
}: {
  storeId: string;
  order: readonly Department[];
}) {
  const [draft, setDraft] = useState<Department[]>([...order]);
  const qc = useQueryClient();

  // Re-seed the draft whenever the server copy changes (e.g. after Save, or a refetch).
  useEffect(() => setDraft([...order]), [order]);

  const dirty = draft.some((d, i) => d !== order[i]);

  const save = useMutation({
    mutationFn: () => api.stores.setPrefs(storeId, { departmentOrder: draft }),
    onSuccess: () => qc.invalidateQueries({ queryKey: storeQueryKeys.detail(storeId) }),
  });

  function move(index: number, dir: -1 | 1) {
    const target = index + dir;
    if (target < 0 || target >= draft.length) return;
    const next = [...draft];
    [next[index], next[target]] = [next[target]!, next[index]!];
    setDraft(next);
  }

  return (
    <div className="flex flex-col gap-3">
      <ul className="flex flex-col divide-y divide-line rounded-lg border border-line">
        {draft.map((dept, i) => (
          <li key={dept} className="flex items-center justify-between gap-2 px-3 py-2">
            <span className="text-sm font-medium text-ink">
              <span className="mr-2 text-xs text-muted">{i + 1}.</span>
              {DEPARTMENT_LABEL[dept]}
            </span>
            <div className="flex gap-1">
              <button
                type="button"
                aria-label={`Move ${DEPARTMENT_LABEL[dept]} up`}
                onClick={() => move(i, -1)}
                disabled={i === 0}
                className="flex size-9 items-center justify-center rounded-lg border border-line text-ink disabled:opacity-30"
              >
                ↑
              </button>
              <button
                type="button"
                aria-label={`Move ${DEPARTMENT_LABEL[dept]} down`}
                onClick={() => move(i, 1)}
                disabled={i === draft.length - 1}
                className="flex size-9 items-center justify-center rounded-lg border border-line text-ink disabled:opacity-30"
              >
                ↓
              </button>
            </div>
          </li>
        ))}
      </ul>
      {save.isError ? <Alert tone="error">{messageForError(save.error)}</Alert> : null}
      {save.isSuccess && !dirty ? <Alert tone="success">Saved.</Alert> : null}
      <Button variant="secondary" onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
        {save.isPending ? 'Saving…' : 'Save walking order'}
      </Button>
    </div>
  );
}
